import { useState, useRef, useCallback } from 'react';
import './ReplyBox.css';

interface ReplyBoxProps {
  onSubmit: (content: string, attachments: File[]) => Promise<void>;
  isSubmitting: boolean;
  needsUserAction: boolean;
}

const MAX_MESSAGE_LENGTH = 2000;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
];

/**
 * ReplyBox - Simple reply mechanism for users
 *
 * Features:
 * - Single text input
 * - File attachment support
 * - Clear submit button
 * - Character counter
 */
export function ReplyBox({ onSubmit, isSubmitting, needsUserAction }: ReplyBoxProps) {
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_MESSAGE_LENGTH) {
      setMessage(value);
      setError(null);
    }
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Submit on Ctrl+Enter or Cmd+Enter
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [message, attachments]
  );

  const validateFile = useCallback((file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `${file.name} is too large. Maximum size is 10MB.`;
    }
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return `${file.name} is not a supported file type.`;
    }
    return null;
  }, []);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files) return;

      const newFiles: File[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const validationError = validateFile(file);
        if (validationError) {
          setError(validationError);
          return;
        }
        newFiles.push(file);
      }

      setAttachments((prev) => [...prev, ...newFiles]);
      setError(null);
    },
    [validateFile]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
      // Reset input so same file can be selected again
      e.target.value = '';
    },
    [handleFiles]
  );

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const removeAttachment = useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = useCallback(async () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage && attachments.length === 0) {
      setError('Please enter a message or attach a file.');
      return;
    }

    try {
      await onSubmit(trimmedMessage, attachments);
      setMessage('');
      setAttachments([]);
      setError(null);
    } catch (err) {
      setError('Failed to send message. Please try again.');
    }
  }, [message, attachments, onSubmit]);

  const openFileDialog = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const canSubmit = (message.trim().length > 0 || attachments.length > 0) && !isSubmitting;

  return (
    <div
      className={`reply-box ${dragActive ? 'reply-box--drag-active' : ''} ${needsUserAction ? 'reply-box--highlighted' : ''}`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <h3 className="reply-box__heading">
        {needsUserAction ? 'Your response is needed' : 'Add a reply'}
      </h3>

      {/* Error message */}
      {error && (
        <div className="reply-box__error" role="alert">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 0a8 8 0 100 16A8 8 0 008 0zm-.75 5a.75.75 0 011.5 0v4a.75.75 0 01-1.5 0V5zm.75 7a.75.75 0 100-1.5.75.75 0 000 1.5z" />
          </svg>
          {error}
        </div>
      )}

      {/* Textarea */}
      <div className="reply-box__input-container">
        <textarea
          ref={textareaRef}
          className="reply-box__textarea"
          placeholder="Type your message here..."
          value={message}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          disabled={isSubmitting}
          maxLength={MAX_MESSAGE_LENGTH}
          rows={4}
          aria-label="Reply message"
        />
        <div className="reply-box__char-count">
          {message.length}/{MAX_MESSAGE_LENGTH}
        </div>
      </div>

      {/* Attachments list */}
      {attachments.length > 0 && (
        <div className="reply-box__attachments" role="list" aria-label="Attached files">
          {attachments.map((file, index) => (
            <div key={`${file.name}-${index}`} className="reply-box__attachment" role="listitem">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                {file.type.startsWith('image/') ? (
                  <path d="M14 3v10a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1h10a1 1 0 011 1zM3 13l3-4 2 2 4-5 3 4V3H3v10z" />
                ) : (
                  <path d="M4 1a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V5l-4-4H4zm5 0v4h4L9 1z" />
                )}
              </svg>
              <span className="reply-box__attachment-name">{file.name}</span>
              <span className="reply-box__attachment-size">{formatFileSize(file.size)}</span>
              <button
                type="button"
                className="reply-box__attachment-remove"
                onClick={() => removeAttachment(index)}
                aria-label={`Remove ${file.name}`}
                disabled={isSubmitting}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 4L4 10M4 4l6 6" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="reply-box__actions">
        <div className="reply-box__left-actions">
          {/* Attach file button */}
          <button
            type="button"
            className="reply-box__attach-btn"
            onClick={openFileDialog}
            disabled={isSubmitting}
            aria-label="Attach file"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2">
              <path
                d="M16.5 9l-7.5 7.5a4.24 4.24 0 01-6-6l7.5-7.5a2.83 2.83 0 014 4L7 14.5a1.41 1.41 0 01-2-2L12.5 5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Attach file
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="reply-box__file-input"
            onChange={handleFileSelect}
            multiple
            accept={ALLOWED_FILE_TYPES.join(',')}
            aria-hidden="true"
          />
        </div>

        <div className="reply-box__right-actions">
          <span className="reply-box__hint">Ctrl+Enter to send</span>
          <button
            type="button"
            className="reply-box__submit-btn"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {isSubmitting ? (
              <>
                <div className="reply-box__spinner" />
                Sending...
              </>
            ) : (
              <>
                Send
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2L7 9M14 2l-5 12-2-5-5-2 12-5z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Drag overlay */}
      {dragActive && (
        <div className="reply-box__drag-overlay">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M16 4v16M8 12l8 8 8-8" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M4 24h24" strokeLinecap="round" />
          </svg>
          <span>Drop files here</span>
        </div>
      )}
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
